// Remove tableBookingRestaurantSettingsService import
// External libraries
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { connect } from "react-redux";

// Styles, constants, icons, api
import styles from "./OrderHistory.module.css";
import {
  OrderFilters,
  FILTER_LIST,
  DATE_FILTER_LIST,
  PAYMENT_FILTER_LIST,
  dateFormat,
  TitleDateFormat,
  FilterDateFormat,
  ListDateFormat,
} from "./constants";
import * as Api from "../../../api";
import { ReactComponent as CalenderIcon } from "../../../assets/images/calender_gray.svg";
import { onSetCartModifierData } from "../../../constants/CommonFunc";
import { utils } from "../../../lib/utils";

// Components
import Container from "../../../components/Container";
import SettingDatePicker from "../../../components/SettingDatePicker";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";
import SearchInput from "../../../components/SearchInput";
import Page from "../../../components/Page";
import SettingSelectField from "../../../components/SettingSelectField";

import OrderHistoryDetailModal from "../../../components/restaurant/OrderHistoryDetailModal";
import { tableBookingRestaurantSettingsService } from '../../../api/useQueryHooks';
import OrderCard from "./OrderListCard";
import NoSearchResult from "./NoSearchResult";

const OrderHistory = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const restaurantId = useParams().rest_id;
  const [orderList, setOrderList] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [orderHistoryDetail, setOrderHistoryDetail] = useState(null);
  const [menuList, setMenuList] = useState([]);
  const [tableList, setTableList] = useState([]);

  useEffect(() => {
    const fetchTableList = async () => {
      const filter = `?orderBy=_id&orderByDir=desc&pageSize=1000&pageNum=1&filter__idRestaurant=${restaurantId}`;
      try {
        const response = await Api.getRestaurantTableList(filter, restaurantId);
        if (response.success) {
          setTableList(response.data.rows || []);
        } else {
          setTableList([]);
        }
      } catch {
        setTableList([]);
      }
    };
    if (restaurantId) fetchTableList();
  }, [restaurantId]);
  const [orderStatusFilter, setOrderStatusFilter] = useState(["completed"]);
  const [showStatusError, setShowStatusError] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [showSearchResult, setShowSearchResult] = useState(false);

  const orderTypeFilterBg = { true: "#000", false: "#FFF" };
  const orderTypeFilterText = {
    true: "#FFF",
    false: "#777777",
  };

  useEffect(() => {
    if (menuList.length) {
      getOrderList(1);
    } else {
      setIsLoading(false);
    }
  }, [orderTypeFilter, dateFilter, dateVal, menuList, orderStatusFilter]);

  useEffect(() => {
    getMenuList();
  }, []);

  const getMenuList = async () => {
    setIsLoading(true);
    let filter = `?pageSize=300000&pageNum=1&filter__idRestaurant=${restaurantId}&orderBy=order&orderByDir=asc`;
    const response = await Api.getMenuList(filter, restaurantId);
    if (response.success) {
      setMenuList(response.data.rows);
    } else {
      setIsLoading(false);
    }
  };

  const getOrderList = (num) => {
    const orderStatusKeys = orderStatusFilter.filter((x) => x !== "all");
    if (orderStatusKeys.length === 0) {
      setShowStatusError(true);
      return;
    }
    const newKey = orderStatusKeys.map(key => `"${key}"`).join(",");
    setIsLoading(true);

    let filter = `pageSize=20&pageNum=${num}&filter__idRestaurant=${restaurantId}&filter_orderStatus=[${newKey}]&orderBy=createdAt&orderByDir=desc&filter_orderType=[${OrderFilters[orderTypeFilter]}]`;

    if (dateFilter != "") {
      let fromDate = "";
      let toDate = "";
      if (dateFilter == "date" && dateVal != "") {
        fromDate = moment(dateVal, dateFormat).format(FilterDateFormat);
        toDate = moment(dateVal, dateFormat).add(1, "days").format(FilterDateFormat);
      } else if (dateFilter == "today") {
        fromDate = moment().format(FilterDateFormat);
        toDate = moment().add(1, "days").format(FilterDateFormat);
      } else if (dateFilter == "this_week") {
        fromDate = moment().startOf("week").format(FilterDateFormat);
        toDate = moment().endOf("week").add(1, "days").format(FilterDateFormat);
      } else if (dateFilter == "this_month") {
        fromDate = moment().startOf("month").format(FilterDateFormat);
        toDate = moment().endOf("month").add(1, "days").format(FilterDateFormat);
      }
      filter =
        filter +
        "&filter_dateRangeFrom=" +
        fromDate +
        "&filter_dateRangeTo=" +
        toDate;
    }

    Api.getOrders(filter).then((response) => {
      if (response.success) {
        let data = response.data.rows;
        setPageNum(num);
        setTotalPages(response.data.pages);
        const list = [];

        const currency = props.restaurantData.currecny ?? utils.getCurrencySymbol(props.restaurantData.currecnyIcon);
        data.map((x, i) => {
          const list_item = modifyOrderListItem(x);
          list.push({
            ...list_item,
            price: { value: x.totalAmount, currency: currency },
          });
        });
        const sortList = [];

        list.forEach((item, index) => {
          let date = moment(item.createdAt).format(TitleDateFormat);
          let dataIndex = sortList.findIndex((x) => x?.date === date);
          if (dataIndex >= 0) {
            sortList[dataIndex]["list"] = [
              ...sortList[dataIndex]["list"],
              item,
            ];
          } else {
            sortList.push({ date: date, list: [item] });
          }
        });
        setOrderList(sortList);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const modifyOrderListItem = (x) => {
    return ({
      ...x,
      date: moment(x.createdAt).format(ListDateFormat),
      cart: x.cart.map((y) => {
        const menuData = menuList.find((z) => z._id === y._idMenuItem) ?? null;
        const updateData = {
          ...y,
          name: y.menuItemName ?? menuData?.name,
          price: y.price ?? menuData?.price,
          cartModifier: onSetCartModifierData(
            y?.modifiers ?? [],
            menuList
          ),
        };

        return updateData;
      }),
    });
  }

  const onSelectPage = (value) => {
    getOrderList(value);
  };

  const handlerDateChange = (val) => {
    setDateFilter("date");
    setDateVal(moment(val).format(dateFormat));
  };

  const onOrderItemClick = (item) => {
    setOrderHistoryDetail(item);
  };

  // By default return "£" symbol if currency not found in CURRENCY_LIST

  const handleOrderStatusFilterChange = (value) => {
    let newValue = value;
    if (orderStatusFilter.includes("all") && value.length !== PAYMENT_FILTER_LIST.length) {
      if (!newValue.includes("all")) newValue = [];
      // find "all" and remove it from the array
      else newValue = value.filter((x) => x !== "all");
    } else if (value.includes("all")) {
      newValue = PAYMENT_FILTER_LIST.map((x) => x.value);
    } else if (!value.includes("all") && value.length === PAYMENT_FILTER_LIST.length - 1) {
      // If all items are selected, add "all" to the array
      newValue = [...value, "all"];
    }

    setShowStatusError(newValue.length === 0);
    setOrderStatusFilter(newValue);
  }

  const handleSearch = () => {
    setIsLoading(true);

    Api.getOrderByRef(searchText).then((response) => {
      if (response.success) {
        setSearchResult(response.data);
      } else {
        setSearchResult(null);
      }
    }).finally(() => {
      setIsLoading(false);
      setShowSearchResult(true);
    });
  }

  const getList = (searchResult) => {
    return [{
      date: moment(searchResult.order.createdAt).format(TitleDateFormat),
      list: [{
        ...modifyOrderListItem(searchResult.order),
        price: { value: searchResult.order.totalAmount, currency: utils.getCurrencySymbol(props.restaurantData.currecnyIcon) },
      }]
    }]
  }

  const getOrderTable = () => {
    const list = showSearchResult ? getList(searchResult) : orderList;
    return list.map((t, i2) => <OrderCard key={i2} data={t} onOrderItemClick={onOrderItemClick} />);
  }

  const resetSearchText = () => {
    setSearchText('');
    setSearchResult(null);
    setShowSearchResult(false);
  }

  return (
    <Container
      page={"restaurants"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      <Grid container item xs={12}>
        <Grid
          item
          container
          xs={12}
          mb={2}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <RestaurantHeader
            title={"Order History"}
            onBackBtnClick={() => navigate("/restaurant/" + restaurantId)}
          />
          <Box
            ml={2}
            className={styles.orderTypeFilterListView}
            style={{ backgroundColor: orderTypeFilterBg[false] }}
          >
            {FILTER_LIST.map((x, i) => {
              return (
                <Box
                  key={i}
                  onClick={() => {
                    setOrderTypeFilter(x._id);
                    setDateFilter("");
                    setDateVal("");
                  }}
                  className={styles.orderTypeFilterView}
                  style={{
                    backgroundColor:
                      orderTypeFilterBg[x._id == orderTypeFilter],
                  }}
                >
                  <Typography
                    className={styles.orderTypeFilterText}
                    style={{
                      color: orderTypeFilterText[x._id == orderTypeFilter],
                    }}
                  >
                    {x.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
        <Grid item xs={12} className={styles.mainView} p={2} pt={3}>
          <Grid container item xs={12} rowSpacing={"0.25rem"} className={styles.toolbarContainer}>
            <Grid item xs={12} md={4} lg={3}>
              <SearchInput
                value={searchText}
                setValue={(val) => setSearchText(val.toUpperCase())}
                placeholder={"Search Order"}
                inputBg={"#FFFFFF"}
                height={48}
                onSearch={handleSearch}
                resetValue={resetSearchText}
                sx={{
                  xs: { maxWidth: '100%' },
                  sm: { maxWidth: '20rem' },
                  border: '1px solid #aaaaaa',
                  borderRadius: '10px',
                  '& .MuiFilledInput-root': { height: '34px' },
                  '& input': {
                    fontSize: '14px',
                    fontFamily: 'GilroySemiBold',
                    color: '#272D2F'
                  },
                  '& .MuiInputBase-input': {
                    padding: 0
                  }
                }}
              />
            </Grid>
            <Grid container item xs={12} md={8} lg={9} rowGap={"0.25rem"} columnGap={"0.25rem"} justifyContent="flex-end">
              <Grid className={styles.orderStatusContainer}>
                <Typography
                  className={styles.orderTypeFilterText}
                  style={{
                    color: "#272D2F",
                    fontSize: 15,
                    fontFamily: "GilroySemiBold",
                  }}
                >
                  Order Status
                </Typography>
                <Box width="19.3125rem" height="2.125rem" position={"relative"}>
                  {orderStatusFilter.length > 0 && <Box sx={{
                    position: 'absolute',
                    top: 'calc(50% - 0.75rem)',
                    left: '0.25rem',
                    zIndex: '10',
                    background: '#000000',
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    color: 'white',
                    fontSize: '0.75rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>{(orderStatusFilter.length === PAYMENT_FILTER_LIST.length ? PAYMENT_FILTER_LIST.length - 1 : orderStatusFilter.length)}</Box>}
                  <SettingSelectField
                    label={""}
                    menuList={PAYMENT_FILTER_LIST}
                    multiSelect={true}
                    value={orderStatusFilter}
                    setValue={handleOrderStatusFilterChange}
                    border={"1px solid #AAA"}
                    placeholder={""}
                    variant={"filled"}
                    sx={{
                      '& .MuiBox-root': {
                        height: '100%',
                      },
                      '& .MuiFilledInput-root': {
                        height: '2.125rem',
                      },
                      '& .MuiFilledInput-input': {
                        padding: '0.25rem 0 0 2rem'
                      }
                    }}
                    containerSx={{
                      '& .MuiInputBase-root': {
                        alignItems: 'flex-start',
                      }
                    }}
                    error={showStatusError && "Select at least one order status"}
                  />
                </Box>
              </Grid>
              <Grid
                className={styles.orderDateFilterListView}
                style={{ backgroundColor: orderTypeFilterBg[false] }}
              >
                {DATE_FILTER_LIST.map((x, i) => {
                  return (
                    <Box key={i} className={styles.dateFilterMainView}>
                      <Box
                        onClick={() => {
                          if (x._id == "date") {
                          } else {
                            setDateFilter(x._id);
                            setDateVal("");
                          }
                        }}
                        className={styles.dateFilterView}
                        style={{
                          backgroundColor:
                            orderTypeFilterBg[x._id == dateFilter],
                        }}
                      >
                        {x._id == "date" &&
                          (dateFilter != "date" ||
                            (dateFilter == "date" && dateVal != "")) ? (
                          <Box
                            style={{
                              width: dateVal == "" ? 30 : 100,
                            }}
                          >
                            <SettingDatePicker
                              value={dateVal}
                              inputFormat={dateFormat}
                              onChange={handlerDateChange}
                              height={"34px"}
                              color={"#FFF"}
                              backgroundColor={
                                orderTypeFilterBg[x._id == dateFilter]
                              }
                              leftView={
                                <Box className="cursor-pointer">
                                  <CalenderIcon width={17} height={17} />
                                </Box>
                              }
                            />
                          </Box>
                        ) : (
                          <Typography
                            className={styles.orderTypeFilterText}
                            style={{
                              color: orderTypeFilterText[x._id == dateFilter],
                            }}
                          >
                            {x.name}
                          </Typography>
                        )}
                      </Box>
                      {i < DATE_FILTER_LIST.length - 1 ? (
                        <Box
                          style={{
                            height: "40%",
                            width: 1,
                            backgroundColor: "#C4C4C4",
                          }}
                        />
                      ) : null}
                    </Box>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={12} className={styles.orderList} sx={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
            {isLoading ? <Loader height={{ height: window.innerHeight - 150 }} /> :
              showSearchResult && !searchResult ?
                <NoSearchResult searchText={searchText} reset={resetSearchText} /> :
                getOrderTable()}
          </Grid>
          {/* Display the pagination interface only for order and not for search */}
          {!showSearchResult && <Grid container mt={3} item xs={12} justifyContent={"center"}>
            {totalPages > 0 ? (
              <Page
                totalPages={totalPages}
                pageNum={pageNum}
                onSelectPage={onSelectPage}
              />
            ) : null}
          </Grid>}
        </Grid>
      </Grid>
      <OrderHistoryDetailModal
        data={orderHistoryDetail}
        tableList={tableList}
        restaurantId={restaurantId}
        onClose={() => setOrderHistoryDetail(null)}
        onRefund={() => {
          setOrderHistoryDetail(null);
          getOrderList(pageNum);
        }}
      />
    </Container >
  );
};
const mapStateToProps = (state) => {
  return {
    restaurantData: state.userData.restaurantData,
  };
};

export default connect(mapStateToProps, null)(OrderHistory);
